import { useEffect, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import {
    getDashboardV3RetentionCohorts,
    getDashboardV3RetentionKpi,
} from '../services/dashboardV3.service';
import type {
    DashboardV3RetentionCohortRow,
    DashboardV3RetentionGranularity,
    DashboardV3RetentionKpiCard,
    DashboardV3RetentionProductType,
    DashboardV3RetentionTrendPoint,
} from '../services/dashboardV3.types';

interface UseDashboardV3RetentionDataParams {
    productType: DashboardV3RetentionProductType;
    granularity: DashboardV3RetentionGranularity;
}

export function useDashboardV3RetentionData({
    productType,
    granularity,
}: UseDashboardV3RetentionDataParams) {
    const notify = useNotify();
    const t = useTranslate();

    const [cards, setCards] = useState<DashboardV3RetentionKpiCard[]>([]);
    const [rows, setRows] = useState<DashboardV3RetentionCohortRow[]>([]);
    const [trend, setTrend] = useState<DashboardV3RetentionTrendPoint[]>([]);
    const [maxPeriod, setMaxPeriod] = useState(0);
    const [dataThrough, setDataThrough] = useState('');

    const [kpiLoading, setKpiLoading] = useState(false);
    const [cohortsLoading, setCohortsLoading] = useState(false);

    /**
     * KPI 卡片固定展示日/周/月三张，只跟产品有关；
     * 切换右上角粒度不需要重新请求。
     */
    useEffect(() => {
        let alive = true;

        setKpiLoading(true);

        getDashboardV3RetentionKpi({ productType })
            .then((resp) => {
                if (!alive) return;
                setCards(resp?.data?.cards ?? []);
            })
            .catch((e: any) => {
                if (!alive) return;
                notify(
                    e?.message ||
                    t('module.dashboardV3.retention.messages.kpiLoadFailed', {
                        _: 'Failed to load retention KPI data',
                    }),
                    { type: 'warning' }
                );
                setCards([]);
            })
            .finally(() => {
                if (!alive) return;
                setKpiLoading(false);
            });

        return () => {
            alive = false;
        };
    }, [productType, notify, t]);

    /**
     * 趋势图与队列矩阵同源，一次请求返回；产品或粒度变化都要重新拉。
     */
    useEffect(() => {
        let alive = true;

        setCohortsLoading(true);

        getDashboardV3RetentionCohorts({ productType, granularity })
            .then((resp) => {
                if (!alive) return;
                setRows(resp?.data?.rows ?? []);
                setTrend(resp?.data?.trend ?? []);
                setMaxPeriod(Number(resp?.data?.maxPeriod ?? 0));
                setDataThrough(resp?.data?.dataThrough ?? '');
            })
            .catch((e: any) => {
                if (!alive) return;
                notify(
                    e?.message ||
                    t('module.dashboardV3.retention.messages.loadFailed', {
                        _: 'Failed to load retention data',
                    }),
                    { type: 'warning' }
                );
                setRows([]);
                setTrend([]);
                setMaxPeriod(0);
                setDataThrough('');
            })
            .finally(() => {
                if (!alive) return;
                setCohortsLoading(false);
            });

        return () => {
            alive = false;
        };
    }, [productType, granularity, notify, t]);

    return {
        cards,
        rows,
        trend,
        maxPeriod,
        dataThrough,

        loading: kpiLoading || cohortsLoading,
        kpiLoading,
        cohortsLoading,
    };
}
